import { Component, createSignal, Show } from "solid-js";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../../contexts/ToastContext";
import { reportIssue } from "../../api/reportIssue";

export interface ReportIssueModalProps {
  open: boolean;
  onClose: () => void;
  correlationId?: string;
  initialTitle?: string;
}

export const ReportIssueModal: Component<ReportIssueModalProps> = (props) => {
  const toast = useToast();
  const [title, setTitle] = createSignal(props.initialTitle ?? "");
  const [description, setDescription] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | undefined>(undefined);

  const handleClose = () => {
    setTitle(props.initialTitle ?? "");
    setDescription("");
    setError(undefined);
    props.onClose();
  };

  const handleSubmit = async () => {
    if (!title().trim()) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await reportIssue({
        title: title().trim(),
        description: description().trim() || undefined,
        correlation_id: props.correlationId,
      });
      toast.showSuccess(`Issue reported: ${result.display_id}`);
      handleClose();
    } catch {
      setError("Failed to report issue. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      title="Report an Issue"
      size="md"
    >
      <div class="flex flex-col gap-4">
        <Input
          label="Title"
          placeholder="Describe the issue briefly"
          value={title()}
          onInput={setTitle}
          maxLength={200}
          autofocus
        />
        <Input
          label="Description (optional)"
          placeholder="Additional details, steps to reproduce, etc."
          value={description()}
          onInput={setDescription}
          isTextarea
          rows={4}
          maxLength={2000}
        />

        <Show when={error()}>
          <p class="text-sm text-signal-red">{error()}</p>
        </Show>

        <div class="flex gap-2 justify-end">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting()}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting()}
            disabled={!title().trim()}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};
